/**
 * Product Swatch Form JavaScript
 * Handles modal interactions, swatch selection, and form submission
 * Uses unique namespace to avoid conflicts
 */

(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    webhookUrl: '',
    thankYouModalDelay: 3000,
  };

  // Store original transform values for motion-element elements (shared across all instances)
  const motionElementTransforms = new Map();

  /**
   * Get elements from container
   */
  function getElements(container) {
    return {
      formModal: container.querySelector('.product-swatch-form__modal--form'),
      thankYouModal: container.querySelector('.product-swatch-form__modal--thankyou'),
      openButton: container.querySelector('[data-action="open-form-modal"]'),
      form: container.querySelector('.product-swatch-form__form'),
      submitButton: container.querySelector('[data-submit-button]'),
    };
  }

  /**
   * Check if form modal is open
   */
  function isFormModalOpen(formModal) {
    return formModal && formModal.classList.contains('is-open');
  }

  /**
   * Check if thank you modal is open
   */
  function isThankYouModalOpen(thankYouModal) {
    return thankYouModal && thankYouModal.classList.contains('is-open');
  }

  /**
   * Set transform unset on motion-element elements
   */
  function setMotionElementTransformUnset() {
    const motionElements = document.querySelectorAll('.motion-element');
    
    motionElements.forEach((element) => {
      if (!motionElementTransforms.has(element)) {
        const computedStyle = window.getComputedStyle(element);
        const originalTransform = computedStyle.transform || 'none';
        motionElementTransforms.set(element, originalTransform);
      }
      
      if (element.style.transform !== 'unset') {
        element.style.transform = 'unset';
      }
    });
  }

  /**
   * Restore transform on motion-element elements
   */
  function restoreMotionElementTransform(formModal, thankYouModal) {
    if (isFormModalOpen(formModal) || isThankYouModalOpen(thankYouModal)) {
      return;
    }

    motionElementTransforms.forEach((originalTransform, element) => {
      if (originalTransform === 'none' || originalTransform === '') {
        element.style.transform = '';
      } else {
        element.style.transform = originalTransform;
      }
    });

    motionElementTransforms.clear();
  }

  /**
   * Open form modal
   */
  function openFormModal(formModal) {
    if (!formModal) return;

    setMotionElementTransformUnset();
    formModal.classList.add('is-open');
    document.body.classList.add('product-swatch-form-modal-open');
  }

  /**
   * Clear all form errors
   */
  function clearAllErrors(container, productId) {
    const errorElements = container.querySelectorAll('.product-swatch-form__error');
    const inputElements = container.querySelectorAll('.product-swatch-form__input, .product-swatch-form__select, .product-swatch-form__textarea');
    
    errorElements.forEach((error) => {
      hideError(error);
    });
    
    inputElements.forEach((input) => {
      input.classList.remove('error');
    });

    // Clear submit error
    clearSubmitError(productId);
  }

  /**
   * Close form modal
   */
  function closeFormModal(formModal, thankYouModal, form, container) {
    if (!formModal) return;

    formModal.classList.remove('is-open');
    
    if (!isThankYouModalOpen(thankYouModal)) {
      document.body.classList.remove('product-swatch-form-modal-open');
      restoreMotionElementTransform(formModal, thankYouModal);
    }
    
    if (form) {
      const productId = container.dataset.productId || '';
      form.reset();
      resetSwatchSelection(container);
      clearAllErrors(container, productId);
    }
  }

  /**
   * Open thank you modal
   */
  function openThankYouModal(thankYouModal) {
    if (!thankYouModal) return;

    setMotionElementTransformUnset();
    thankYouModal.classList.add('is-open');
    document.body.classList.add('product-swatch-form-modal-open');

    setTimeout(() => {
      closeThankYouModal(thankYouModal);
    }, CONFIG.thankYouModalDelay);
  }

  /**
   * Close thank you modal
   */
  function closeThankYouModal(thankYouModal, formModal) {
    if (!thankYouModal) return;

    thankYouModal.classList.remove('is-open');
    document.body.classList.remove('product-swatch-form-modal-open');
    restoreMotionElementTransform(formModal, thankYouModal);
  }

  /**
   * Handle swatch selection (toggle multiple selections)
   */
  function handleSwatchSelection(selectedItem, container) {
    const swatchType = selectedItem.dataset.swatchType;
    if (!swatchType) return;

    selectedItem.classList.toggle('is-selected');
    updateSwatchInput(container, swatchType);
  }

  /**
   * Update hidden input with all selected swatches for a given type
   */
  function updateSwatchInput(container, swatchType) {
    const selectedSwatches = container.querySelectorAll(
      `.product-swatch-form__swatch-item[data-swatch-type="${swatchType}"].is-selected`
    );
    
    const selectedValues = Array.from(selectedSwatches)
      .map(item => item.dataset.swatchValue)
      .filter(Boolean);

    const selectedCdnLinks = Array.from(selectedSwatches)
      .map(item => item.dataset.swatchCdnLink || '')
      .filter(Boolean);

    const hiddenInput = container.querySelector(
      `.product-swatch-form__swatch-input[data-swatch-input="${swatchType}"]`
    );
    if (hiddenInput) {
      hiddenInput.value = selectedValues.join(',');
    }

    const cdnInput = container.querySelector(
      `.product-swatch-form__swatch-input[data-swatch-input="${swatchType}_cdn"]`
    );
    if (cdnInput) {
      cdnInput.value = selectedCdnLinks.join(',');
    }
  }


  /**
   * Reset swatch selection
   */
  function resetSwatchSelection(container) {
    const selectedSwatches = container.querySelectorAll(
      '.product-swatch-form__swatch-item.is-selected'
    );
    selectedSwatches.forEach((item) => {
      item.classList.remove('is-selected');
    });

    const hiddenInputs = container.querySelectorAll(
      '.product-swatch-form__swatch-input'
    );
    hiddenInputs.forEach((input) => {
      input.value = '';
    });
  }

  /**
   * Validation functions
   */
  function validateName(name) {
    if (!name || name.trim() === '') {
      return 'Name is required';
    }
    return '';
  }

  function validateEmail(email) {
    if (!email || email.trim() === '') {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    return '';
  }

  function validateMobile(mobile) {
    const trimmedMobile = mobile ? mobile.trim() : '';
    if (trimmedMobile === '') {
      return 'Mobile is required';
    }
    // Remove any non-digit characters for validation
    const digitsOnly = trimmedMobile.replace(/\D/g, '');
    if (digitsOnly.length !== 10) {
      return 'Mobile number must be exactly 10 digits';
    }
    return '';
  }

  function validateAddress(address) {
    if (!address || address.trim() === '') {
      return 'Delivery Address Line 1 is required';
    }
    return '';
  }

  function validateSuburb(suburb) {
    if (!suburb || suburb.trim() === '') {
      return 'Suburb is required';
    }
    return '';
  }

  function validatePostcode(postcode) {
    if (!postcode || postcode.trim() === '') {
      return 'Postcode is required';
    }
    if (!/^[0-9]{4}$/.test(postcode.trim())) {
      return 'Postcode must be 4 digits';
    }
    return '';
  }

  /**
   * Show error message
   */
  function showError(errorElement, message, inputElement) {
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
    }
    if (inputElement) {
      inputElement.classList.add('error');
    }
  }

  /**
   * Hide error message
   */
  function hideError(errorElement, inputElement) {
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.style.display = 'none';
    }
    if (inputElement) {
      inputElement.classList.remove('error');
    }
  }

  /**
   * Validate a single form field
   */
  function validateField(form, productId, fieldName, validator) {
    const value = new FormData(form).get(fieldName) || '';
    const input = form.querySelector(`#${fieldName}-${productId}`);
    const error = document.getElementById(`${fieldName}-error-${productId}`);
    const errorMsg = validator(value);
    
    if (errorMsg) {
      showError(error, errorMsg, input);
      return false;
    }
    hideError(error, input);
    return true;
  }

  /**
   * Validate form fields
   */
  function validateForm(form, productId) {
    const validations = [
      { field: 'name', validator: validateName },
      { field: 'email', validator: validateEmail },
      { field: 'mobile', validator: validateMobile },
      { field: 'address_line_1', validator: validateAddress },
      { field: 'suburb', validator: validateSuburb },
      { field: 'postcode', validator: validatePostcode }
    ];

    return validations.every(({ field, validator }) => 
      validateField(form, productId, field, validator)
    );
  }

  /**
   * Handle form submission
   */
  async function handleFormSubmit(e, form, submitButton, formModal, thankYouModal, container) {
    e.preventDefault();

    if (!form) {
      console.error('Product Swatch Form: Form not found');
      return;
    }

    // Get product ID from container
    const productId = container.dataset.productId || '';

    // Validate form before submission
    if (!validateForm(form, productId)) {
      const firstError = container.querySelector('.product-swatch-form__error[style*="block"]');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!CONFIG.webhookUrl) {
      console.error('Product Swatch Form: Webhook URL not configured');
      alert('Error: Webhook URL is not configured. Please contact the site administrator.');
      return;
    }

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting...';
    }

    try {
      const formData = new FormData(form);
      
      // Get product reference: Product_title (variant_id) from URL, fallback to first variant
      const urlParams = new URLSearchParams(window.location.search);
      const variantIdFromUrl = urlParams.get('variant') || '';
      const firstVariantId = container.dataset.productFirstVariantId || '';
      const variantId = variantIdFromUrl || firstVariantId;
      const productTitle = container.dataset.productTitle || '';
      const productReference = (productTitle && variantId) ? `${productTitle} (${variantId})` : '';
      
      const payload = {
        name: formData.get('name') || '',
        email: formData.get('email') || '',
        mobile: formData.get('mobile') || '',
        address_line_1: formData.get('address_line_1') || '',
        address_line_2: formData.get('address_line_2') || '',
        suburb: formData.get('suburb') || '',
        postcode: formData.get('postcode') || '',
        where_used: formData.get('where_used') || '',
        additional_info: formData.get('additional_info') || '',
        material_swatch: formData.get('material_swatch') || '',
        fabric_swatch: formData.get('fabric_swatch') || '',
        fabric_cdn_link: formData.get('fabric_cdn_link') || '',
        material_cdn_link: formData.get('material_cdn_link') || '',
        product_reference: productReference,
      };

      let response;
      let responseData = null;
      
      try {
        response = await fetch(CONFIG.webhookUrl, {
          method: 'POST',
          body: JSON.stringify(payload),
        });

        if (response.status === 401) {
          throw new Error('401 Unauthorized: Please check your Google Apps Script deployment settings. The script must be deployed with "Who has access: Anyone" (not "Only myself").');
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Parse response JSON
        try {
          responseData = await response.json();
        } catch (parseError) {
          console.warn('Failed to parse response as JSON', parseError);
          // If response is not JSON, assume success for backward compatibility
          responseData = { status: 'okay' };
        }
      } catch (fetchError) {
        if (fetchError.message.includes('CORS') || fetchError.message.includes('Failed to fetch')) {
          console.warn('CORS error detected, retrying with no-cors mode...');
          // With no-cors mode, we can't read the response, so assume it worked
          // This is a fallback for Google Apps Script compatibility
          responseData = { status: 'okay' };
        } else {
          throw fetchError;
        }
      }

      const submitErrorElement = document.getElementById(`submit-error-${productId}`);
      
      if (responseData && responseData.status === 'okay') {
        if (submitErrorElement) {
          submitErrorElement.textContent = '';
          submitErrorElement.style.display = 'none';
        }
        window.location.href = '/pages/thank-you-sample';
      } else {
        const errorMessage = responseData?.message || 'There was an error submitting your request. Please try again.';
        if (submitErrorElement) {
          submitErrorElement.textContent = errorMessage;
          submitErrorElement.style.display = 'block';
          submitErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          alert(errorMessage);
        }
      }

    } catch (error) {
      console.error('Product Swatch Form: Submission error', error);
      
      const submitErrorElement = document.getElementById(`submit-error-${productId}`);
      let errorMessage = 'There was an error submitting your request. Please try again.';
      
      if (error.message?.includes('401')) {
        errorMessage = 'Authentication error (401): Please check your Google Apps Script deployment settings. The web app must be deployed with "Who has access: Anyone" (not "Only myself").';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      if (submitErrorElement) {
        submitErrorElement.textContent = errorMessage;
        submitErrorElement.style.display = 'block';
        submitErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        alert(errorMessage);
      }
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Submit Request';
      }
    }
  }

  /**
   * Clear submit error message
   */
  function clearSubmitError(productId) {
    const submitErrorElement = document.getElementById(`submit-error-${productId}`);
    if (submitErrorElement) {
      submitErrorElement.textContent = '';
      submitErrorElement.style.display = 'none';
    }
  }

  /**
   * Bind events to a container
   */
  function bindEvents(container, elements) {
    const { formModal, thankYouModal, openButton, form, submitButton } = elements;

    // Open form modal
    if (openButton) {
      openButton.addEventListener('click', (e) => {
        e.preventDefault();
        openFormModal(formModal);
      });
    }

    // Close form modal
    const closeButtons = container.querySelectorAll('[data-action="close-modal"]');
    closeButtons.forEach((button) => {
      button.addEventListener('click', () => {
        closeFormModal(formModal, thankYouModal, form, container);
      });
    });

    // Close thank you modal
    const closeThankYouButtons = container.querySelectorAll('[data-action="close-thankyou-modal"]');
    closeThankYouButtons.forEach((button) => {
      button.addEventListener('click', () => {
        closeThankYouModal(thankYouModal, formModal);
      });
    });

    // Swatch selection
    const swatchItems = container.querySelectorAll('.product-swatch-form__swatch-item');
    swatchItems.forEach((item) => {
      item.addEventListener('click', () => {
        handleSwatchSelection(item, container);
      });

      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSwatchSelection(item, container);
        }
      });
    });

    // Form submission
    if (form) {
      form.addEventListener('submit', (e) => {
        // Clear any previous submit error when form is submitted
        clearSubmitError(productId);
        handleFormSubmit(e, form, submitButton, formModal, thankYouModal, container);
      });

      // Real-time validation helper
      const productId = container.dataset.productId || '';
      const bindValidation = (fieldName, validator, hasInputHandler = false) => {
        const input = form.querySelector(`#${fieldName}-${productId}`);
        if (!input) return;

        const errorId = `${fieldName}-error-${productId}`;
        
        input.addEventListener('blur', () => {
          const error = document.getElementById(errorId);
          const errorMsg = validator(input.value);
          if (errorMsg) {
            showError(error, errorMsg, input);
          } else {
            hideError(error, input);
          }
        });

        if (hasInputHandler) {
          input.addEventListener('input', () => {
            const error = document.getElementById(errorId);
            if (error && error.style.display === 'block') {
              const errorMsg = validator(input.value);
              if (errorMsg) {
                showError(error, errorMsg, input);
              } else {
                hideError(error, input);
              }
            }
          });
        }
      };

      // Mobile input: restrict to digits only
      const mobileInput = form.querySelector(`#mobile-${productId}`);
      if (mobileInput) {
        mobileInput.addEventListener('input', (e) => {
          e.target.value = e.target.value.replace(/\D/g, '');
        });
        bindValidation('mobile', validateMobile);
      }

      const postcodeInput = form.querySelector(`#postcode-${productId}`);
      if (postcodeInput) {
        postcodeInput.addEventListener('input', (e) => {
          e.target.value = e.target.value.replace(/\D/g, '').slice(0, 4);
        });
      }

      // Bind validation for other fields
      bindValidation('name', validateName, true);
      bindValidation('email', validateEmail, true);
      bindValidation('address_line_1', validateAddress, true);
      bindValidation('suburb', validateSuburb, true);
      bindValidation('postcode', validatePostcode, true);
    }

    // Close modal on overlay click
    container.querySelectorAll('.product-swatch-form__modal-overlay').forEach((overlay) => {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          const modal = overlay.closest('.product-swatch-form__modal');
          if (modal?.classList.contains('product-swatch-form__modal--form')) {
            closeFormModal(formModal, thankYouModal, form, container);
          } else if (modal?.classList.contains('product-swatch-form__modal--thankyou')) {
            closeThankYouModal(thankYouModal, formModal);
          }
        }
      });
    });

    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (isFormModalOpen(formModal)) {
          closeFormModal(formModal, thankYouModal, form, container);
        } else if (isThankYouModalOpen(thankYouModal)) {
          closeThankYouModal(thankYouModal, formModal);
        }
      }
    });
  }

  /**
   * Initialize a single product swatch form container
   */
  function initProductSwatchForm(container) {
    // Get webhook URL from data attribute if available
    if (container.dataset.webhookUrl) {
      CONFIG.webhookUrl = container.dataset.webhookUrl;
    }

    const elements = getElements(container);

    if (!elements.formModal || !elements.thankYouModal || !elements.openButton || !elements.form) {
      return;
    }

    bindEvents(container, elements);
  }

  /**
   * Initialize all product swatch forms on page load
   */
  function initProductSwatchForms() {
    const containers = document.querySelectorAll('.product-swatch-form');
    containers.forEach((container) => {
      initProductSwatchForm(container);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductSwatchForms);
  } else {
    initProductSwatchForms();
  }

  // Expose for dynamic content
  if (typeof window !== 'undefined') {
    window.initProductSwatchForms = initProductSwatchForms;
  }
})();
